import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { InquiryForm } from "@/components/InquiryForm";
import {
  Clock, Wifi, BadgeIndianRupee, GraduationCap, CheckCircle2,
  Briefcase, ChevronRight,
} from "lucide-react";

export const CourseDetail = () => {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;
  if (!course) return (
    <div className="max-w-7xl mx-auto px-6 py-20">
      <p className="text-slate-700">Course not found.</p>
      <Link to="/courses" className="btn-outline mt-4">Back to courses</Link>
    </div>
  );

  return (
    <div data-testid="course-detail-page">
      <section className="bg-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <span className="eyebrow" data-testid="course-detail-eyebrow">Flagship Program</span>
            <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-semibold text-[#0A3B32] tracking-tight leading-[1.05]" data-testid="course-detail-title">
              {course.title}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">{course.tagline}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
                <Clock size={16} className="text-[#0A3B32]" />
                <span className="text-sm font-medium">{course.duration}</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
                <Wifi size={16} className="text-[#0A3B32]" />
                <span className="text-sm font-medium">{course.mode}</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#C85A40] text-white rounded-lg px-4 py-2.5">
                <BadgeIndianRupee size={16} />
                <span className="text-sm font-bold">Reserve Free</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
                <GraduationCap size={16} className="text-[#0A3B32]" />
                <span className="text-sm font-medium">Any Graduate / Diploma</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <img
              src="https://images.pexels.com/photos/10987574/pexels-photo-10987574.jpeg"
              alt="Medical device close-up"
              className="rounded-2xl w-full h-[360px] object-cover shadow-xl"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
        {/* LEFT: Content */}
        <div className="lg:col-span-7 space-y-12">
          <div>
            <h2 className="font-heading text-3xl font-semibold text-[#0A3B32]">About this course</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{course.description}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              <b className="text-slate-800">Eligibility:</b> {course.eligibility}
            </p>
          </div>

          <div>
            <h2 className="font-heading text-3xl font-semibold text-[#0A3B32]">Syllabus</h2>
            <div className="mt-6 space-y-4" data-testid="course-syllabus">
              {course.modules.map((m, i) => (
                <div key={i} className="card-soft" data-testid={`module-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex w-9 h-9 rounded-lg bg-[#0A3B32] text-white items-center justify-center text-sm font-semibold">
                      {i + 1}
                    </span>
                    <h3 className="font-heading text-lg font-semibold text-[#0A3B32]">{m.title}</h3>
                  </div>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                    {m.topics.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-sm text-slate-600">
                        <ChevronRight size={16} className="text-[#C85A40] mt-0.5 shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-3xl font-semibold text-[#0A3B32]">Outcomes</h2>
            <ul className="mt-6 space-y-3" data-testid="course-outcomes">
              {course.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="text-[#C85A40] mt-0.5" size={20} />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-3xl font-semibold text-[#0A3B32]">Placement Partners</h2>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {course.placement_partners.map((p) => (
                <div key={p} className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-center text-sm font-medium text-slate-700">
                  <Briefcase size={16} className="mx-auto text-[#0A3B32] mb-1" />
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Sticky enroll card */}
        <aside className="lg:col-span-5">
          <div className="sticky top-28 space-y-4">
            <div className="card-soft" data-testid="enroll-card">
              <p className="eyebrow">Reserve your seat</p>
              <h3 className="font-heading mt-2 text-2xl font-semibold text-[#0A3B32]">
                Free Seat Reservation
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                Submit the form and our counsellor will call you within 24 hours to walk you through fees and the next batch.
              </p>
              <div className="mt-5">
                <InquiryForm courseSlug={course.slug} compact />
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};
